import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, createApiClient } from '../App';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  SpinnerGap,
  ForkKnife,
  CurrencyCircleDollar,
  Star,
  MapPin,
  Trophy,
  ChartBar,
  Download,
  CalendarBlank,
  TrendUp,
  ShareNetwork,
  Heart,
  Image as ImageIcon,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Canvas helper: filled rounded rectangle
function roundRectFill(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}

const Wrapped = () => {
  const { token } = useAuth();
  const api = createApiClient(token);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [generating, setGenerating] = useState(false);
  const [generatingReview, setGeneratingReview] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const fetchWrapped = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/stats/wrapped?year=${year}`);
        setData(res.data);
      } catch (error) {
        console.error('Wrapped error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchWrapped();
  }, [year]);

  const generateWrappedCard = useCallback(async () => {
    if (!data?.has_data) return;
    setGenerating(true);
    try {
      const W = 1080, H = 1920;
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');

      // Background gradient
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, '#1A1918');
      grad.addColorStop(0.4, '#2C2826');
      grad.addColorStop(1, '#4B5945');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // Decorative elements
      ctx.fillStyle = 'rgba(169, 68, 56, 0.15)';
      ctx.beginPath();
      ctx.arc(W * 0.85, H * 0.12, 200, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(196, 163, 90, 0.1)';
      ctx.beginPath();
      ctx.arc(W * 0.15, H * 0.88, 250, 0, Math.PI * 2);
      ctx.fill();

      // Header
      ctx.fillStyle = '#A94438';
      ctx.font = '600 28px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText('MY YEAR IN DINING', W / 2, 120);

      ctx.fillStyle = '#FFFFFF';
      ctx.font = '800 120px system-ui';
      ctx.fillText(`${year}`, W / 2, 270);

      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = '500 32px system-ui';
      ctx.fillText('WRAPPED', W / 2, 330);

      // Divider
      ctx.strokeStyle = 'rgba(196, 163, 90, 0.4)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(W * 0.2, 380);
      ctx.lineTo(W * 0.8, 380);
      ctx.stroke();

      // Stats
      const stats = [
        { label: 'TOTAL VISITS', value: `${data.total_visits || 0}` },
        { label: 'RESTAURANTS', value: `${data.unique_restaurants || 0}` },
        { label: 'TOTAL SPENT', value: `R${((data.total_spend || 0) / 1000).toFixed(1)}k` },
        { label: 'AVG RATING', value: `${(data.avg_rating || 0).toFixed(1)} / 5` },
      ];

      let y = 460;
      stats.forEach((stat) => {
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.font = '600 24px system-ui';
        ctx.textAlign = 'center';
        ctx.fillText(stat.label, W / 2, y);
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '800 72px system-ui';
        ctx.fillText(stat.value, W / 2, y + 80);
        y += 150;
      });

      // Top restaurant
      if (data.top_restaurants?.[0]) {
        y += 20;
        ctx.fillStyle = '#C4A35A';
        ctx.font = '600 24px system-ui';
        ctx.fillText('FAVOURITE RESTAURANT', W / 2, y);
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '700 48px system-ui';
        const topName = data.top_restaurants[0].name || '';
        ctx.fillText(topName.length > 25 ? topName.slice(0, 25) + '...' : topName, W / 2, y + 60);
      }

      // Top cuisine
      if (data.top_cuisines?.[0]) {
        y += 120;
        ctx.fillStyle = '#C4A35A';
        ctx.font = '600 24px system-ui';
        ctx.fillText('TOP CUISINE', W / 2, y);
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '700 48px system-ui';
        ctx.fillText(data.top_cuisines[0].name || '', W / 2, y + 60);
      }

      // Top suburb
      if (data.top_suburbs?.[0]) {
        y += 120;
        ctx.fillStyle = '#C4A35A';
        ctx.font = '600 24px system-ui';
        ctx.fillText('FAVOURITE AREA', W / 2, y);
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '700 48px system-ui';
        ctx.fillText(data.top_suburbs[0].name || '', W / 2, y + 60);
      }

      // Monthly trend as mini bar chart
      if (data.monthly_trend?.length > 0) {
        y += 140;
        ctx.fillStyle = '#C4A35A';
        ctx.font = '600 24px system-ui';
        ctx.fillText('MONTHLY TREND', W / 2, y);

        const barY = y + 30;
        const barW = 50;
        const gap = 20;
        const maxH = 120;
        const maxCount = Math.max(...data.monthly_trend.map(m => m.count), 1);
        const startX = (W - 12 * (barW + gap) + gap) / 2;

        data.monthly_trend.forEach((m, i) => {
          const bx = startX + i * (barW + gap);
          const bh = (m.count / maxCount) * maxH;
          ctx.fillStyle = m.count > 0 ? 'rgba(196, 163, 90, 0.7)' : 'rgba(255,255,255,0.1)';
          ctx.fillRect(bx, barY + maxH - bh, barW, Math.max(bh, 4));
          ctx.fillStyle = 'rgba(255,255,255,0.4)';
          ctx.font = '400 16px system-ui';
          ctx.fillText(['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'][i], bx + barW / 2, barY + maxH + 22);
        });
      }

      // Footer
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.font = '400 22px system-ui';
      ctx.fillText('Dining Tracker · Cape Town', W / 2, H - 60);

      // Download
      const link = document.createElement('a');
      link.download = `dining-wrapped-${year}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Wrapped card downloaded!');
    } catch (error) {
      console.error('Share card error:', error);
      toast.error('Failed to generate share card');
    } finally {
      setGenerating(false);
    }
  }, [data, year]);

  // ── Year in Review Card (1080x1080 Instagram) ──────────────────────
  const generateYearInReview = useCallback(async () => {
    if (!data?.has_data) return;
    setGeneratingReview(true);
    try {
      const S = 1080;
      const canvas = document.createElement('canvas');
      canvas.width = S;
      canvas.height = S;
      const ctx = canvas.getContext('2d');

      // ── Background ──
      ctx.fillStyle = '#1A1918';
      ctx.fillRect(0, 0, S, S);

      // Grain texture
      ctx.fillStyle = 'rgba(255,255,255,0.012)';
      for (let i = 0; i < 600; i++) {
        ctx.beginPath();
        ctx.arc(Math.random() * S, Math.random() * S, Math.random() * 1.5 + 0.3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Accent glow
      ctx.fillStyle = 'rgba(169, 68, 56, 0.12)';
      ctx.beginPath(); ctx.arc(S * 0.9, S * 0.1, 200, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = 'rgba(75, 89, 69, 0.1)';
      ctx.beginPath(); ctx.arc(S * 0.08, S * 0.95, 220, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = 'rgba(196, 163, 90, 0.06)';
      ctx.beginPath(); ctx.arc(S * 0.5, S * 0.5, 300, 0, Math.PI * 2); ctx.fill();

      // ── Top strip — branding ──
      ctx.fillStyle = '#A94438';
      ctx.font = '700 16px system-ui';
      ctx.textAlign = 'left';
      ctx.fillText('DINING TRACKER', 60, 55);
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.font = '400 16px system-ui';
      ctx.textAlign = 'right';
      ctx.fillText('Cape Town', S - 60, 55);

      // Thin gold line
      ctx.strokeStyle = 'rgba(196, 163, 90, 0.3)';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(60, 72); ctx.lineTo(S - 60, 72); ctx.stroke();

      // ── Title ──
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.45)';
      ctx.font = '600 20px system-ui';
      ctx.fillText('YEAR IN REVIEW', S / 2, 115);

      ctx.fillStyle = '#FFFFFF';
      ctx.font = '800 88px system-ui';
      ctx.fillText(`${year}`, S / 2, 210);

      // ── 4 hero stats in 2x2 grid ──
      const statGrid = [
        { label: 'Visits', value: `${data.total_visits || 0}`, color: '#FFFFFF' },
        { label: 'Restaurants', value: `${data.unique_restaurants || 0}`, color: '#FFFFFF' },
        { label: 'Avg Rating', value: data.average_rating ? `${data.average_rating}` : '—', color: '#C4A35A' },
        { label: 'Spent', value: data.total_spend > 0 ? `R${(data.total_spend / 1000).toFixed(1)}k` : '—', color: '#FFFFFF' },
      ];

      const gridY = 260;
      const cellW = (S - 120 - 30) / 2;
      const cellH = 90;
      statGrid.forEach((st, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const cx = 60 + col * (cellW + 30) + cellW / 2;
        const cy = gridY + row * (cellH + 12);

        // Card bg
        ctx.fillStyle = 'rgba(255,255,255,0.04)';
        roundRectFill(ctx, 60 + col * (cellW + 30), cy, cellW, cellH, 16);

        // Value
        ctx.fillStyle = st.color;
        ctx.font = '800 42px system-ui';
        ctx.textAlign = 'center';
        ctx.fillText(st.value, cx, cy + 48);

        // Label
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        ctx.font = '500 14px system-ui';
        ctx.fillText(st.label.toUpperCase(), cx, cy + 74);
      });

      // ── Top restaurant ──
      let curY = gridY + 2 * (cellH + 12) + 30;
      if (data.top_restaurants?.[0]) {
        ctx.fillStyle = '#C4A35A';
        ctx.font = '600 13px system-ui';
        ctx.textAlign = 'left';
        ctx.fillText('TOP RESTAURANT', 60, curY);

        ctx.fillStyle = '#FFFFFF';
        ctx.font = '700 30px system-ui';
        const trName = data.top_restaurants[0].name || '';
        ctx.fillText(trName.length > 28 ? trName.slice(0, 28) + '...' : trName, 60, curY + 36);

        // Visit count + rating
        const trMeta = [];
        if (data.top_restaurants[0].visit_count_this_year) trMeta.push(`${data.top_restaurants[0].visit_count_this_year} visits`);
        if (data.top_restaurants[0].aggregate_rating) trMeta.push(`${data.top_restaurants[0].aggregate_rating} stars`);
        if (trMeta.length > 0) {
          ctx.fillStyle = 'rgba(255,255,255,0.4)';
          ctx.font = '400 16px system-ui';
          ctx.fillText(trMeta.join(' · '), 60, curY + 58);
        }
        curY += 80;
      }

      // ── Top 3 dishes ──
      const dishes = (data.top_dishes || []).slice(0, 3);
      if (dishes.length > 0) {
        curY += 8;
        ctx.fillStyle = '#C4A35A';
        ctx.font = '600 13px system-ui';
        ctx.textAlign = 'left';
        ctx.fillText('FAVOURITE DISHES', 60, curY);
        curY += 24;

        dishes.forEach((d, i) => {
          ctx.fillStyle = 'rgba(255,255,255,0.06)';
          roundRectFill(ctx, 60, curY, S - 120, 36, 18);

          ctx.fillStyle = '#FFFFFF';
          ctx.font = '600 16px system-ui';
          ctx.textAlign = 'left';
          const dName = d.name.length > 30 ? d.name.slice(0, 30) + '...' : d.name;
          ctx.fillText(dName, 80, curY + 24);

          ctx.fillStyle = 'rgba(196, 163, 90, 0.8)';
          ctx.font = '400 14px system-ui';
          ctx.textAlign = 'right';
          ctx.fillText(`x${d.count}`, S - 80, curY + 24);

          curY += 44;
        });
      }

      // ── Cuisine breakdown ──
      const cuisines = (data.top_cuisines || []).slice(0, 5);
      if (cuisines.length > 0) {
        curY += 8;
        ctx.fillStyle = '#C4A35A';
        ctx.font = '600 13px system-ui';
        ctx.textAlign = 'left';
        ctx.fillText('CUISINE MIX', 60, curY);
        curY += 20;

        const totalCuisineCount = cuisines.reduce((s, c) => s + c.count, 0);
        const barFullW = S - 120;
        const barHeight = 18;

        // Stacked horizontal bar
        const barColors = ['#A94438', '#C4A35A', '#4B5945', '#2563eb', '#ea580c'];
        let barX = 60;
        cuisines.forEach((c, i) => {
          const segW = Math.max((c.count / totalCuisineCount) * barFullW, 2);
          ctx.fillStyle = barColors[i % barColors.length];
          if (i === 0) {
            roundRectFill(ctx, barX, curY, segW, barHeight, 9);
          } else if (i === cuisines.length - 1) {
            roundRectFill(ctx, barX, curY, segW, barHeight, 9);
          } else {
            ctx.fillRect(barX, curY, segW, barHeight);
          }
          barX += segW;
        });
        curY += barHeight + 10;

        // Legend dots
        ctx.textAlign = 'left';
        let legX = 60;
        cuisines.forEach((c, i) => {
          ctx.fillStyle = barColors[i % barColors.length];
          ctx.beginPath(); ctx.arc(legX + 5, curY + 5, 5, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = 'rgba(255,255,255,0.6)';
          ctx.font = '400 13px system-ui';
          const pct = `${Math.round((c.count / totalCuisineCount) * 100)}%`;
          ctx.fillText(`${c.name} ${pct}`, legX + 16, curY + 10);
          legX += ctx.measureText(`${c.name} ${pct}`).width + 30;
          if (legX > S - 100) { legX = 60; curY += 22; }
        });
        curY += 20;
      }

      // ── Monthly sparkline ──
      if (data.monthly_trend?.length > 0) {
        curY += 12;
        ctx.fillStyle = '#C4A35A';
        ctx.font = '600 13px system-ui';
        ctx.textAlign = 'left';
        ctx.fillText('MONTHLY VISITS', 60, curY);
        curY += 16;

        const sparkW = S - 120;
        const sparkH = 48;
        const maxC = Math.max(...data.monthly_trend.map(m => m.count), 1);
        const points = data.monthly_trend.map((m, i) => ({
          x: 60 + (i / 11) * sparkW,
          y: curY + sparkH - (m.count / maxC) * sparkH,
        }));

        // Fill area
        ctx.beginPath();
        ctx.moveTo(points[0].x, curY + sparkH);
        points.forEach(p => ctx.lineTo(p.x, p.y));
        ctx.lineTo(points[points.length - 1].x, curY + sparkH);
        ctx.closePath();
        const areaGrad = ctx.createLinearGradient(0, curY, 0, curY + sparkH);
        areaGrad.addColorStop(0, 'rgba(196, 163, 90, 0.25)');
        areaGrad.addColorStop(1, 'rgba(196, 163, 90, 0)');
        ctx.fillStyle = areaGrad;
        ctx.fill();

        // Line
        ctx.beginPath();
        points.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
        ctx.strokeStyle = '#C4A35A';
        ctx.lineWidth = 2.5;
        ctx.stroke();

        // Dots on non-zero months
        points.forEach((p, i) => {
          if (data.monthly_trend[i].count > 0) {
            ctx.fillStyle = '#C4A35A';
            ctx.beginPath(); ctx.arc(p.x, p.y, 4, 0, Math.PI * 2); ctx.fill();
          }
        });

        // Month labels
        curY += sparkH + 8;
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.font = '400 11px system-ui';
        ctx.textAlign = 'center';
        ['J','F','M','A','M','J','J','A','S','O','N','D'].forEach((l, i) => {
          ctx.fillText(l, 60 + (i / 11) * sparkW, curY + 10);
        });
      }

      // ── Footer ──
      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      ctx.font = '400 14px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText('diningtracker.co.za', S / 2, S - 36);

      // Download
      const link = document.createElement('a');
      link.download = `year-in-review-${year}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Year in Review card downloaded!');
    } catch (error) {
      console.error('Year in review card error:', error);
      toast.error('Failed to generate card');
    } finally {
      setGeneratingReview(false);
    }
  }, [data, year]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <SpinnerGap className="w-10 h-10 animate-spin text-[#A94438]" />
      </div>
    );
  }

  if (!data?.has_data) {
    return (
      <div className="container-app py-16 text-center">
        <ForkKnife weight="duotone" className="w-16 h-16 text-[#D4CFC8] mx-auto mb-4" />
        <h2 className="text-2xl font-heading font-semibold text-[#1A1918] mb-2">No dining data for {year}</h2>
        <p className="text-[#7A7873]">Start logging visits to unlock your Dining Wrapped!</p>
        <div className="flex justify-center gap-2 mt-6">
          {[2024, 2025, 2026].map(y => (
            <Button key={y} variant={y === year ? 'default' : 'outline'} onClick={() => setYear(y)} className={y === year ? 'btn-primary' : ''}>
              {y}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  const maxMonthlyCount = Math.max(...(data.monthly_trend || []).map(m => m.count), 1);

  return (
    <div className="pb-8" data-testid="wrapped-page">
      {/* Hero */}
      <div className="wrapped-hero text-center py-16 px-4" data-testid="wrapped-hero">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-[#A94438] font-medium text-sm tracking-widest uppercase mb-2">Your Year in Dining</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold text-[#1A1918] mb-4">{year} Wrapped</h1>
          <div className="flex justify-center gap-2">
            {[2024, 2025, 2026].map(y => (
              <button
                key={y}
                onClick={() => setYear(y)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${y === year ? 'bg-[#4B5945] text-white' : 'bg-[#F5F1EC] text-[#5A5752] hover:bg-[#EAE8E3]'}`}
                data-testid={`year-btn-${y}`}
              >
                {y}
              </button>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Button
              onClick={generateWrappedCard}
              disabled={generating}
              className="bg-[#A94438] hover:bg-[#8B362C] text-white"
              data-testid="share-wrapped-btn"
            >
              {generating ? (
                <SpinnerGap className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <ShareNetwork weight="bold" className="w-5 h-5 mr-2" />
              )}
              {generating ? 'Generating...' : 'Share Story'}
            </Button>
            <Button
              onClick={generateYearInReview}
              disabled={generatingReview}
              className="bg-[#1A1918] hover:bg-[#2C2826] text-white border border-[#C4A35A]/30"
              data-testid="year-in-review-btn"
            >
              {generatingReview ? (
                <SpinnerGap className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <ImageIcon weight="bold" className="w-5 h-5 mr-2" />
              )}
              {generatingReview ? 'Generating...' : 'Year in Review'}
            </Button>
          </div>
        </motion.div>
      </div>

      <div className="container-app py-8 space-y-8" ref={contentRef}>
        {/* Hero stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
          initial="hidden" animate="visible"
          variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }}
        >
          <StatCard icon={ForkKnife} label="Total Visits" value={data.total_visits} delay={0} />
          <StatCard icon={MapPin} label="Unique Restaurants" value={data.unique_restaurants} delay={0.1} />
          <StatCard icon={CurrencyCircleDollar} label="Total Spend" value={`R${data.total_spend?.toLocaleString()}`} delay={0.2} />
          <StatCard icon={Star} label="Avg Rating" value={data.average_rating ? `${data.average_rating}/5` : '—'} delay={0.3} />
        </motion.div>

        {/* Monthly Activity Chart */}
        <motion.div className="card-elevated card-padding" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <h2 className="font-heading text-xl font-semibold mb-6 flex items-center gap-2">
            <ChartBar weight="duotone" className="w-5 h-5 text-[#A94438]" />
            Monthly Activity
          </h2>
          <div className="flex items-end gap-2 h-40" data-testid="monthly-chart">
            {(data.monthly_trend || []).map((m, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-[#9A9590]">{m.count || ''}</span>
                <div
                  className="w-full rounded-t-md transition-all duration-500"
                  style={{
                    height: `${Math.max((m.count / maxMonthlyCount) * 100, 4)}%`,
                    backgroundColor: m.count > 0 ? '#4B5945' : '#E8DCD0',
                  }}
                />
                <span className="text-xs text-[#7A7873]">{MONTHS[idx]}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Top Restaurants */}
          {data.top_restaurants?.length > 0 && (
            <motion.div className="card-elevated card-padding" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}>
              <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Trophy weight="duotone" className="w-5 h-5 text-[#C4A35A]" />
                Top Restaurants
              </h2>
              <div className="space-y-3" data-testid="top-restaurants">
                {data.top_restaurants.map((r, idx) => (
                  <div key={r.id} className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${idx === 0 ? 'bg-[#C4A35A] text-white' : idx === 1 ? 'bg-[#9A9590] text-white' : 'bg-[#E8DCD0] text-[#5A5752]'}`}>
                      {idx + 1}
                    </span>
                    {r.hero_image_url ? (
                      <img src={r.hero_image_url} alt={r.name} className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-[#E8DCD0] flex items-center justify-center"><ForkKnife className="w-5 h-5 text-[#9A9590]" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1A1918] text-sm truncate">{r.name}</p>
                      <p className="text-xs text-[#9A9590]">{r.visit_count_this_year} visits · {r.suburb || 'Cape Town'}</p>
                    </div>
                    {r.aggregate_rating && (
                      <div className="flex items-center gap-1 text-sm text-[#C4A35A]">
                        <Star weight="fill" className="w-3.5 h-3.5" />
                        {r.aggregate_rating}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Top Cuisines & Suburbs */}
          <motion.div className="card-elevated card-padding" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
            <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
              <TrendUp weight="duotone" className="w-5 h-5 text-[#A94438]" />
              Your Tastes
            </h2>

            {data.top_cuisines?.length > 0 && (
              <div className="mb-5">
                <p className="text-xs text-[#9A9590] uppercase tracking-wide mb-2">Top Cuisines</p>
                <div className="flex flex-wrap gap-2" data-testid="top-cuisines">
                  {data.top_cuisines.map(c => (
                    <span key={c.name} className="px-3 py-1.5 bg-[#F5F1EC] text-[#5A5752] rounded-full text-sm">
                      {c.name} <span className="text-[#9A9590]">({c.count})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {data.top_suburbs?.length > 0 && (
              <div className="mb-5">
                <p className="text-xs text-[#9A9590] uppercase tracking-wide mb-2">Top Suburbs</p>
                <div className="space-y-2" data-testid="top-suburbs">
                  {data.top_suburbs.map((s, idx) => {
                    const maxCount = data.top_suburbs[0]?.count || 1;
                    return (
                      <div key={s.name} className="flex items-center gap-3">
                        <MapPin weight="duotone" className="w-4 h-4 text-[#7A7873] flex-shrink-0" />
                        <span className="text-sm text-[#5A5752] w-28 truncate">{s.name}</span>
                        <div className="flex-1 h-2 bg-[#F5F1EC] rounded-full overflow-hidden">
                          <div className="h-full bg-[#4B5945] rounded-full transition-all" style={{ width: `${(s.count / maxCount) * 100}%` }} />
                        </div>
                        <span className="text-xs text-[#9A9590] w-6 text-right">{s.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {data.top_dishes?.length > 0 && (
              <div>
                <p className="text-xs text-[#9A9590] uppercase tracking-wide mb-2">Most Ordered</p>
                <div className="flex flex-wrap gap-2" data-testid="top-dishes">
                  {data.top_dishes.slice(0, 6).map(d => (
                    <span key={d.name} className="px-3 py-1.5 bg-[#E8DCD0] text-[#5A5752] rounded-full text-sm">
                      {d.name} <span className="text-[#9A9590]">x{d.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Rating trend */}
        {data.monthly_trend?.some(m => m.avg_rating) && (
          <motion.div className="card-elevated card-padding" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
            <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
              <Star weight="duotone" className="w-5 h-5 text-[#C4A35A]" />
              Rating Trend
            </h2>
            <div className="flex items-end gap-2 h-32" data-testid="rating-trend">
              {data.monthly_trend.map((m, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                  {m.avg_rating && <span className="text-xs text-[#C4A35A] font-medium">{m.avg_rating}</span>}
                  <div
                    className="w-full rounded-t-md transition-all duration-500"
                    style={{
                      height: m.avg_rating ? `${(m.avg_rating / 5) * 100}%` : '4%',
                      backgroundColor: m.avg_rating ? '#C4A35A' : '#F5F1EC',
                    }}
                  />
                  <span className="text-xs text-[#7A7873]">{MONTHS[idx]}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Spend summary */}
        {data.total_spend > 0 && (
          <motion.div className="card-elevated card-padding text-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
            <p className="text-xs text-[#9A9590] uppercase tracking-wide mb-1">Total Spent on Dining in {year}</p>
            <p className="text-4xl font-heading font-bold text-[#1A1918]" data-testid="total-spend-big">R{data.total_spend?.toLocaleString()}</p>
            {data.total_tip > 0 && (
              <p className="text-sm text-[#7A7873] mt-1">Including R{data.total_tip?.toLocaleString()} in tips</p>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};


const StatCard = ({ icon: Icon, label, value, delay }) => (
  <motion.div
    className="card-elevated card-padding text-center"
    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
    transition={{ delay }}
  >
    <Icon weight="duotone" className="w-8 h-8 text-[#A94438] mx-auto mb-2" />
    <p className="text-2xl font-heading font-bold text-[#1A1918]">{value}</p>
    <p className="text-xs text-[#9A9590] mt-1">{label}</p>
  </motion.div>
);

export default Wrapped;
